import { assign } from 'xstate'
import { get } from 'lodash'
import { redirect, setSessionAuthMethod } from '@dg-util'
import CONFIG, { FlowName } from '../../config'
import {
  FLOW_NAME_QUERY_KEY,
  getFlowName,
  getProductName,
  processSignOut,
  switchFSM,
} from '@dg-util/productHandler'
import { AuthMethod } from '../../constants/auth-constants'
import messagesQueue, { MessageType } from '../../service/messagesQueue'
import BrandConfig from 'BrandConfig'
import { FSMService } from '..'
import { FSMCommonTransitions } from './constants'
import { ContextType, EventType } from './config'

// v5 passes a single `{ context, event }` argument to actions instead of `(context, event)`.
type ActionArg = { context: ContextType; event: EventType }

const setUserAuthorized = assign({
  authorized: true,
})

// Also captures `errorType` off the event, because the `error` state's BACK guards need it and
// v5 removed `meta.state.event`, the v4 source that survived a payload-less BACK. Written
// unconditionally so a payload-less ERROR cannot leave a previous error's type behind.
const setBackPath = assign({
  backPath: ({ event }: ActionArg) => get(event, 'state'),
  errorType: ({ event }: ActionArg) => get(event, 'errorType'),
})

const getSignOutTargetState = (flowName: FlowName) => {
  if (flowName === FlowName.MY_PAGES) {
    return {
      [FSMCommonTransitions.AUTH_BANK_ID]: FSMCommonTransitions.AUTH_BANK_ID_START,
    }
  }

  return FSMCommonTransitions.AUTH
}

const signOut = ({ context }: ActionArg) => {
  ;(async () => {
    const signOutResult = await processSignOut()

    // Successfull sign out
    if (signOutResult) {
      // Redirects handling
      redirectOnSignOut(context)
    }
  })()

  // NB: v4 ignored the return value of a plain action function, so the `assign` this used to
  // return never applied. The context reset happens via `switchFSM`'s `contextOverride` in
  // `redirectOnSignOut`. Dropped rather than activated, to keep behaviour identical.
}

const setRegularAuth = assign({
  authMethod: () => {
    setSessionAuthMethod(AuthMethod.REGULAR)
    return AuthMethod.REGULAR
  },
})

const setVippsAuth = assign({
  authMethod: () => {
    // Set Vipps auth method to verify it in services (has no access to hooks or FSM context)
    setSessionAuthMethod(AuthMethod.VIPPS)
    return AuthMethod.VIPPS
  },
})

const setBankIdAuth = assign({
  authMethod: () => {
    setSessionAuthMethod(AuthMethod.BANKID)
    return AuthMethod.BANKID
  },
})

const setAgentAuth = assign({
  authMethod: () => {
    setSessionAuthMethod(AuthMethod.AGENT)
    return AuthMethod.AGENT
  },
})

const switchFlow = () => {
  const flow = sessionStorage.getItem(FLOW_NAME_QUERY_KEY)
  if (flow === FlowName.MY_PAGES) {
    redirect(`/?flow=${FlowName.QUOTE_AND_BUY}`)
  }
}

// Redirects to agent portal in case the auth method is AGENT
const redirectToAgentPortalFor = (context: ContextType) => {
  if (context?.authMethod === AuthMethod.AGENT) {
    // Redirect with pending timeout, so the original action will have time to finish its processing
    setTimeout(() => {
      redirect(CONFIG.AGENT_PORTAL_HOST)
    }, 500)
  }
}

// Handle Brand / product / flow specific redirect on sign out
const redirectOnSignOut = (context: ContextType) => {
  // Check if it is an agent flow and redirect to the Agent Portal if necessary
  redirectToAgentPortalFor(context)

  // Redirect for My Pages
  const flowName = getFlowName()

  if (flowName === FlowName.MY_PAGES) {
    const productName = getProductName()
    const redirectLinkMap = BrandConfig.MYPAGES_LOGOUT_REDIRECT
    const redirectLink = redirectLinkMap[productName as keyof typeof redirectLinkMap]

    if (redirectLink) {
      redirect(redirectLink)
    }
  }

  // Cleanup of app-state with usage of SwitchFSM
  const targetProduct = getProductName()
  const targetFlow = getFlowName()
  const targetStateValue = getSignOutTargetState(flowName)

  switchFSM({
    targetStateValue,
    targetFlow,
    targetProduct,
    FSMService,
    contextOverride: { authorized: false, authMethod: '', prevConfig: undefined },
  })
}

// Set event in message queue for usage after cleanup and page reload
const setMessageQueueEvent = ({ event }: ActionArg) => {
  messagesQueue.setMessage({
    id: event.type,
    message: event as unknown as MessageType['message'],
  })
}

export default {
  setUserAuthorized,
  setBackPath,
  signOut,
  switchFlow,
  setVippsAuth,
  setBankIdAuth,
  setAgentAuth,
  setRegularAuth,
  redirectToAgentPortal: ({ context }: ActionArg) => redirectToAgentPortalFor(context),
  setMessageQueueEvent,
}
